import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { AlertTriangle, CheckCircle2, Download, Edit, Eye, Loader2, Plus, Save, Search, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { driverIncidentReportService } from '../services/driverIncidentReportService';
import { driverService } from '../services/driverService';
import { vehicleService } from '../services/vehicleService';
import { trailerService } from '../services/trailerService';
import { documentService } from '../services/documentService';

type WorkflowStatus = 'todo' | 'in_progress' | 'service_notified' | 'completed';
type VehicleKind = 'tractor' | 'trailer';
type ReportType = 'accident' | 'vehicle-general-breakdown' | 'seyir-mobile' | 'general-maintenance' | 'damage';

type IncidentRow = {
  id: string;
  driverId: string;
  driverUserId: string;
  driverName: string;
  driverPhone: string;
  reportType: ReportType;
  title: string;
  description: string;
  workflowStatus: WorkflowStatus;
  vehicleKind: VehicleKind;
  vehicleId: string | null;
  trailerId: string | null;
  vehiclePlate: string;
  trailerPlate: string;
  incidentDate: string | null;
  city: string;
  district: string;
  workStartAt: string | null;
  workEndAt: string | null;
  estimatedCost: number;
  invoiceAmount: number;
  invoiceNumber: string;
  invoiceDocumentId: string | null;
  createdAt: string;
  updatedAt: string;
};

type DriverRow = {
  id: string;
  name: string;
  phone: string;
  assignedVehicleId: string;
  assignedVehiclePlate: string;
  assignedTrailerPlate: string;
};

type VehicleRow = {
  id: string;
  plate: string;
  driverName: string;
  assignedDriverId: string;
  linkedTrailerPlate: string;
};

type TrailerRow = {
  id: string;
  plate: string;
  linkedVehicleId: string;
  linkedVehiclePlate: string;
};

const REPORT_TYPE_OPTIONS: Array<{ value: ReportType; label: string }> = [
  { value: 'accident', label: 'Kaza' },
  { value: 'vehicle-general-breakdown', label: 'Araç Genel Arıza' },
  { value: 'seyir-mobile', label: 'Seyir Mobil' },
  { value: 'general-maintenance', label: 'Genel Bakım' },
  { value: 'damage', label: 'Hasar' },
];

const STATUS_OPTIONS: Array<{ value: WorkflowStatus; label: string; className: string }> = [
  { value: 'todo', label: 'Yapılacak', className: 'bg-rose-500 text-white' },
  { value: 'in_progress', label: 'Yapılıyor', className: 'bg-amber-400 text-white' },
  { value: 'service_notified', label: 'Servise Bildirildi', className: 'bg-sky-400 text-white' },
  { value: 'completed', label: 'Tamamlandı', className: 'bg-emerald-500 text-white' },
];

const defaultForm = {
  vehicleKind: 'tractor' as VehicleKind,
  vehicleId: '',
  trailerId: '',
  driverId: '',
  driverUserId: '',
  driverName: '',
  driverPhone: '',
  reportType: 'vehicle-general-breakdown' as ReportType,
  title: '',
  incidentDate: new Date().toISOString().slice(0, 16),
  city: '',
  district: '',
  workStartAt: '',
  workEndAt: '',
  estimatedCost: '',
  invoiceAmount: '',
  invoiceNumber: '',
  description: '',
  workflowStatus: 'todo' as WorkflowStatus,
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('tr-TR');
};

const getStatusOption = (status: WorkflowStatus) => STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];

export function DriverIncidentReports() {
  const [rows, setRows] = useState<IncidentRow[]>([]);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [trailers, setTrailers] = useState<TrailerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<IncidentRow | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [invoiceDocs, setInvoiceDocs] = useState<any[]>([]);
  const [incidentDocs, setIncidentDocs] = useState<any[]>([]);
  const [filter, setFilter] = useState<'all' | WorkflowStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState<'all' | 'with-invoice' | 'without-invoice'>('all');
  const [detailRow, setDetailRow] = useState<IncidentRow | null>(null);
  const [detailInvoiceDocs, setDetailInvoiceDocs] = useState<any[]>([]);
  const [detailIncidentDocs, setDetailIncidentDocs] = useState<any[]>([]);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reportsRes, driversRes, vehiclesRes, trailersRes] = await Promise.all([
        driverIncidentReportService.getReports(filter === 'all' ? undefined : { status: filter }),
        driverService.getDrivers({ type: 'internal' }),
        vehicleService.getVehicles(),
        trailerService.getTrailers(),
      ]);

      setRows(Array.isArray(reportsRes?.data) ? reportsRes.data : []);
      setDrivers(Array.isArray(driversRes?.data) ? driversRes.data.map((item: any) => ({
        id: String(item.id),
        name: item.full_name || item.name || '-',
        phone: item.phone || '',
        assignedVehicleId: item.assigned_vehicle_id ? String(item.assigned_vehicle_id) : '',
        assignedVehiclePlate: item.assigned_vehicle_plate || '',
        assignedTrailerPlate: item.assigned_trailer_plate || '',
      })) : []);
      setVehicles(Array.isArray(vehiclesRes?.data) ? vehiclesRes.data.map((item: any) => ({
        id: String(item.id),
        plate: item.cekici_plaka || item.plate || '-',
        driverName: item.driver_name || '',
        assignedDriverId: item.assigned_driver_id ? String(item.assigned_driver_id) : '',
        linkedTrailerPlate: item.linked_trailer_plate || item.dorse_plaka || '',
      })) : []);
      setTrailers(Array.isArray(trailersRes?.data) ? trailersRes.data.map((item: any) => ({
        id: String(item.id),
        plate: item.plate || '-',
        linkedVehicleId: item.linked_vehicle_id ? String(item.linked_vehicle_id) : '',
        linkedVehiclePlate: item.linked_vehicle_plate || '',
      })) : []);
    } catch (error: any) {
      console.error('Driver incident reports load failed:', error);
      toast.error(error?.message || 'Kaza/arıza kayıtları alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [filter]);

  const stats = useMemo(() => ({
    total: rows.length,
    todo: rows.filter((row) => row.workflowStatus === 'todo').length,
    inProgress: rows.filter((row) => row.workflowStatus === 'in_progress').length,
    serviceNotified: rows.filter((row) => row.workflowStatus === 'service_notified').length,
    completed: rows.filter((row) => row.workflowStatus === 'completed').length,
  }), [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesSearch = !searchTerm.trim() || [
        row.vehiclePlate,
        row.trailerPlate,
        row.driverName,
        row.driverPhone,
        row.title,
        row.description,
        row.invoiceNumber,
      ].join(' ').toLocaleLowerCase('tr-TR').includes(searchTerm.trim().toLocaleLowerCase('tr-TR'));

      const hasInvoice = Boolean(row.invoiceDocumentId || row.invoiceAmount > 0 || row.invoiceNumber);
      const matchesInvoice = invoiceFilter === 'all'
        ? true
        : invoiceFilter === 'with-invoice'
          ? hasInvoice
          : !hasInvoice;

      return matchesSearch && matchesInvoice;
    });
  }, [rows, searchTerm, invoiceFilter]);

  const exportExcel = () => {
    const data = filteredRows.map((row) => ({
      'Araç Tipi': row.vehicleKind === 'trailer' ? 'Dorse' : 'Çekici',
      'Çekici': row.vehiclePlate || '',
      'Dorse': row.trailerPlate || '',
      'Sürücü': row.driverName || '',
      'Telefon': row.driverPhone || '',
      'Arıza Tipi': REPORT_TYPE_OPTIONS.find((item) => item.value === row.reportType)?.label || row.reportType,
      'Arıza Kayıt Tarihi': formatDateTime(row.incidentDate || row.createdAt),
      'İl': row.city || '',
      'İlçe': row.district || '',
      'İşlem Başlama': formatDateTime(row.workStartAt),
      'İşlem Bitiş': formatDateTime(row.workEndAt),
      'Beklenen Maliyet': row.estimatedCost,
      'Fatura Tutarı': row.invoiceAmount,
      'Fatura No': row.invoiceNumber || '',
      'Servis Faturası': row.invoiceDocumentId || row.invoiceNumber ? 'Geldi' : 'Gelmedi',
      'Aşama': getStatusOption(row.workflowStatus).label,
      'Açıklama': row.description || '',
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Kaza_Ariza');
    XLSX.writeFile(workbook, `kaza-ariza-kayitlari-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const hydrateByVehicleSelection = (nextVehicleKind: VehicleKind, nextId: string) => {
    if (nextVehicleKind === 'tractor') {
      const vehicle = vehicles.find((item) => item.id === nextId);
      const driver = drivers.find((item) => item.id === vehicle?.assignedDriverId || item.assignedVehicleId === nextId);
      setForm((current) => ({
        ...current,
        vehicleKind: 'tractor',
        vehicleId: nextId,
        trailerId: '',
        driverId: driver?.id || current.driverId,
        driverName: driver?.name || vehicle?.driverName || current.driverName,
        driverPhone: driver?.phone || current.driverPhone,
        title: current.title || `${REPORT_TYPE_OPTIONS.find((item) => item.value === current.reportType)?.label || 'Kayıt'} - ${vehicle?.plate || ''}`,
      }));
      return;
    }

    const trailer = trailers.find((item) => item.id === nextId);
    const driver = drivers.find((item) => item.assignedVehicleId === String(trailer?.linkedVehicleId || ''));
    setForm((current) => ({
      ...current,
      vehicleKind: 'trailer',
      trailerId: nextId,
      vehicleId: trailer?.linkedVehicleId || '',
      driverId: driver?.id || current.driverId,
      driverName: driver?.name || current.driverName,
      driverPhone: driver?.phone || current.driverPhone,
      title: current.title || `${REPORT_TYPE_OPTIONS.find((item) => item.value === current.reportType)?.label || 'Kayıt'} - ${trailer?.plate || ''}`,
    }));
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
    setForm(defaultForm);
    setInvoiceFile(null);
    setImageFiles([]);
    setInvoiceDocs([]);
    setIncidentDocs([]);
    setVehicleSearch('');
    setVehicleDropdownOpen(false);
  };

  const openCreate = () => {
    closeModal();
    setModalOpen(true);
  };

  const openEdit = async (row: IncidentRow) => {
    setEditingRow(row);
    setForm({
      vehicleKind: row.vehicleKind,
      vehicleId: row.vehicleId || '',
      trailerId: row.trailerId || '',
      driverId: row.driverId,
      driverUserId: row.driverUserId,
      driverName: row.driverName,
      driverPhone: row.driverPhone,
      reportType: row.reportType,
      title: row.title || '',
      incidentDate: row.incidentDate ? String(row.incidentDate).slice(0, 16) : '',
      city: row.city || '',
      district: row.district || '',
      workStartAt: row.workStartAt ? String(row.workStartAt).slice(0, 16) : '',
      workEndAt: row.workEndAt ? String(row.workEndAt).slice(0, 16) : '',
      estimatedCost: String(row.estimatedCost || ''),
      invoiceAmount: String(row.invoiceAmount || ''),
      invoiceNumber: row.invoiceNumber || '',
      description: row.description || '',
      workflowStatus: row.workflowStatus,
    });
    setInvoiceFile(null);
    setImageFiles([]);
    setModalOpen(true);

    try {
      const [invoiceRes, imageRes] = await Promise.all([
        documentService.getDocuments({ entityType: 'driver_incident_report', entityId: row.id, documentType: 'incident-invoice' }),
        documentService.getDocuments({ entityType: 'driver_incident_report', entityId: row.id, documentType: 'incident-image' }),
      ]);
      setInvoiceDocs(Array.isArray(invoiceRes?.data) ? invoiceRes.data : []);
      setIncidentDocs(Array.isArray(imageRes?.data) ? imageRes.data : []);
    } catch (error) {
      console.error('Incident documents load failed:', error);
      setInvoiceDocs([]);
      setIncidentDocs([]);
    }
  };

  const openDetail = async (row: IncidentRow) => {
    setDetailRow(row);
    try {
      const [invoiceRes, imageRes] = await Promise.all([
        documentService.getDocuments({ entityType: 'driver_incident_report', entityId: row.id, documentType: 'incident-invoice' }),
        documentService.getDocuments({ entityType: 'driver_incident_report', entityId: row.id, documentType: 'incident-image' }),
      ]);
      setDetailInvoiceDocs(Array.isArray(invoiceRes?.data) ? invoiceRes.data : []);
      setDetailIncidentDocs(Array.isArray(imageRes?.data) ? imageRes.data : []);
    } catch (error) {
      console.error('Incident detail documents load failed:', error);
      setDetailInvoiceDocs([]);
      setDetailIncidentDocs([]);
    }
  };

  const uploadReportDocuments = async (reportId: string) => {
    let invoiceDocumentId: string | null = null;

    if (invoiceFile) {
      const formData = new FormData();
      formData.append('file', invoiceFile);
      formData.append('entityType', 'driver_incident_report');
      formData.append('entityId', reportId);
      formData.append('documentType', 'incident-invoice');
      formData.append('name', invoiceFile.name);
      const upload = await documentService.uploadDocument(formData);
      invoiceDocumentId = upload?.data?.id ? String(upload.data.id) : null;
    }

    for (const file of imageFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', 'driver_incident_report');
        formData.append('entityId', reportId);
        formData.append('documentType', 'incident-image');
        formData.append('name', file.name);
        await documentService.uploadDocument(formData);
      } catch (err: any) {
        console.error(`Incident image upload failed for ${file.name}:`, err);
        toast.error(`Görsel yüklenemedi: ${file.name}`);
      }
    }

    return invoiceDocumentId;
  };

  const saveReport = async () => {
    if (!form.description.trim()) {
      toast.error('Açıklama zorunludur');
      return;
    }
    if (form.vehicleKind === 'tractor' && !form.vehicleId) {
      toast.error('Çekici seçmelisiniz');
      return;
    }
    if (form.vehicleKind === 'trailer' && !form.trailerId) {
      toast.error('Dorse seçmelisiniz');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        vehicleKind: form.vehicleKind,
        vehicleId: form.vehicleId || undefined,
        trailerId: form.trailerId || undefined,
        driverId: form.driverId || undefined,
        driverUserId: form.driverUserId || undefined,
        driverName: form.driverName || undefined,
        driverPhone: form.driverPhone || undefined,
        reportType: form.reportType,
        title: form.title || undefined,
        incidentDate: form.incidentDate || undefined,
        city: form.city || undefined,
        district: form.district || undefined,
        workStartAt: form.workStartAt || undefined,
        workEndAt: form.workEndAt || undefined,
        estimatedCost: Number(form.estimatedCost || 0),
        invoiceAmount: Number(form.invoiceAmount || 0),
        invoiceNumber: form.invoiceNumber || undefined,
        description: form.description,
        workflowStatus: form.workflowStatus,
      };

      if (editingRow) {
        const response: any = await driverIncidentReportService.updateReport(editingRow.id, payload);
        if (!response?.success) throw new Error(response?.message || 'Kayıt güncellenemedi');
        const invoiceDocumentId = await uploadReportDocuments(editingRow.id);
        if (invoiceDocumentId) {
          await driverIncidentReportService.updateReport(editingRow.id, { ...payload, invoiceDocumentId });
        }
      } else {
        const response: any = await driverIncidentReportService.createReport(payload);
        if (!response?.success || !response?.data?.id) throw new Error(response?.message || 'Kayıt oluşturulamadı');
        const reportId = String(response.data.id);
        const invoiceDocumentId = await uploadReportDocuments(reportId);
        if (invoiceDocumentId) {
          await driverIncidentReportService.updateReport(reportId, { ...payload, invoiceDocumentId });
        }
      }

      toast.success(editingRow ? 'Kayıt güncellendi' : 'Kayıt oluşturuldu');
      closeModal();
      await loadData();
    } catch (error: any) {
      console.error('Incident save failed:', error);
      toast.error(error?.message || 'Kayıt kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id: string, status: WorkflowStatus) => {
    try {
      const response: any = await driverIncidentReportService.updateStatus(id, status);
      if (!response?.success) throw new Error(response?.message || 'Durum güncellenemedi');
      toast.success(response?.message || 'Durum güncellendi');
      await loadData();
    } catch (error: any) {
      console.error('Incident status update failed:', error);
      toast.error(error?.message || 'Durum güncellenemedi');
    }
  };

  const deleteRow = async (row: IncidentRow) => {
    if (!window.confirm(`"${row.title || 'Kayıt'}" silinecek. Emin misiniz?`)) return;
    try {
      const response: any = await driverIncidentReportService.deleteReport(row.id);
      if (!response?.success) throw new Error(response?.message || 'Silinemedi');
      toast.success(response?.message || 'Kayıt silindi');
      if (detailRow?.id === row.id) setDetailRow(null);
      await loadData();
    } catch (error: any) {
      console.error('Incident delete failed:', error);
      toast.error(error?.message || 'Kayıt silinemedi');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kaza / Arıza Kayıtları</h1>
          <p className="mt-1 text-gray-500">Araç teslimi sonrası oluşan arıza, kaza, hasar ve bakım kayıtlarını yönetin.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportExcel} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50">
            <Download className="h-5 w-5" /> Excel
          </button>
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
            <Plus className="h-5 w-5" /> Yeni Kayıt
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {STATUS_OPTIONS.map((item) => {
          const value = item.value === 'todo' ? stats.todo : item.value === 'in_progress' ? stats.inProgress : item.value === 'service_notified' ? stats.serviceNotified : stats.completed;
          return <div key={item.value} className="rounded-xl border border-slate-200 bg-white p-4"><div className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${item.className}`}>{item.label}</div><div className="mt-3 text-3xl font-bold text-slate-900">{value}</div></div>;
        })}
      </div>

      <div className="flex gap-2">
        <div className="relative min-w-[320px] flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Plaka, sürücü, telefon, açıklama ara" className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm" />
        </div>
        <button type="button" onClick={() => setFilter('all')} className={`rounded-lg px-4 py-2 text-sm font-medium ${filter === 'all' ? 'bg-slate-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}>Tümü</button>
        {STATUS_OPTIONS.map((item) => (
          <button key={item.value} type="button" onClick={() => setFilter(item.value)} className={`rounded-lg px-4 py-2 text-sm font-medium ${filter === item.value ? 'bg-slate-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}>{item.label}</button>
        ))}
        <select value={invoiceFilter} onChange={(e) => setInvoiceFilter(e.target.value as typeof invoiceFilter)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="all">Servis faturası: Tümü</option>
          <option value="with-invoice">Servis faturası geldi</option>
          <option value="without-invoice">Servis faturası gelmedi</option>
        </select>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Araç</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Sürücü</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Telefon</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Arıza Tipi</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Tarih</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Konum</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Beklenen Maliyet</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Aşama</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-blue-600" />Yükleniyor...</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">Kayıt bulunamadı.</td></tr>
              ) : filteredRows.map((row) => {
                const status = getStatusOption(row.workflowStatus);
                const issueType = REPORT_TYPE_OPTIONS.find((item) => item.value === row.reportType)?.label || row.reportType;
                return (
                  <tr key={row.id}>
                    <td className="px-4 py-3">{row.vehicleKind === 'trailer' ? 'Dorse' : 'Çekici'} • {row.vehicleKind === 'trailer' ? row.trailerPlate : row.vehiclePlate}</td>
                    <td className="px-4 py-3"><div className="font-medium text-gray-900">{row.driverName || '-'}</div><div className="mt-1 text-xs text-gray-500 truncate max-w-xs">{row.description}</div></td>
                    <td className="px-4 py-3">{row.driverPhone || '-'}</td>
                    <td className="px-4 py-3">{issueType}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(row.incidentDate || row.createdAt)}</td>
                    <td className="px-4 py-3">{[row.city, row.district].filter(Boolean).join(' / ') || '-'}</td>
                    <td className="px-4 py-3 text-right">₺{Number(row.estimatedCost || 0).toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-3"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${status.className}`}>{status.label}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={() => void openDetail(row)} className="rounded-lg p-2 text-sky-600 hover:bg-sky-50" title="Detay"><Eye className="h-4 w-4" /></button>
                        <button type="button" onClick={() => openEdit(row)} className="rounded-lg p-2 text-blue-600 hover:bg-blue-50" title="Düzenle"><Edit className="h-4 w-4" /></button>
                        <button type="button" onClick={() => deleteRow(row)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" title="Sil"><Trash2 className="h-4 w-4" /></button>
                        {STATUS_OPTIONS.filter((item) => item.value !== row.workflowStatus).map((item) => (
                          <button key={item.value} type="button" onClick={() => updateStatus(row.id, item.value)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-50" title={item.label}>{item.value === 'completed' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : item.value === 'service_notified' ? <AlertTriangle className="h-4 w-4 text-sky-600" /> : <Eye className="h-4 w-4 text-amber-600" />}</button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">{editingRow ? 'Kayıt Düzenle' : 'Yeni Kaza / Arıza Kaydı'}</h2>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <div className="space-y-6 p-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Araç Tipi</label>
                  <select value={form.vehicleKind} onChange={(e) => setForm((current) => ({ ...current, vehicleKind: e.target.value as VehicleKind, vehicleId: '', trailerId: '', driverId: '', driverName: '', driverPhone: '' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                    <option value="tractor">Çekici</option>
                    <option value="trailer">Dorse</option>
                  </select>
                </div>
                <div className="relative">
                  <label className="mb-2 block text-sm font-medium text-gray-700">Araç Seçimi</label>
                  <div className="relative">
                    <input
                      value={vehicleSearch || (form.vehicleKind === 'tractor'
                        ? vehicles.find((v) => v.id === form.vehicleId)?.plate || ''
                        : trailers.find((t) => t.id === form.trailerId)?.plate || '')}
                      onChange={(e) => { setVehicleSearch(e.target.value); setVehicleDropdownOpen(true); }}
                      onFocus={() => setVehicleDropdownOpen(true)}
                      placeholder={form.vehicleKind === 'tractor' ? 'Plaka veya sürücü ile ara...' : 'Dorse plaka ara...'}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                    {vehicleDropdownOpen && (
                      <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                        {(form.vehicleKind === 'tractor' ? vehicles : trailers)
                          .filter((item) => {
                            const q = vehicleSearch.toLocaleLowerCase('tr-TR');
                            if (!q) return true;
                            const plate = form.vehicleKind === 'tractor'
                              ? (item as VehicleRow).plate
                              : (item as TrailerRow).plate;
                            const searchable = plate + ' ' + ((form.vehicleKind === 'tractor' ? (item as VehicleRow).driverName : '') || '');
                            return searchable.toLocaleLowerCase('tr-TR').includes(q);
                          })
                          .map((item) => (
                            <button key={item.id} type="button"
                              onClick={() => {
                                setVehicleSearch('');
                                setVehicleDropdownOpen(false);
                                if (form.vehicleKind === 'tractor') {
                                  hydrateByVehicleSelection('tractor', item.id);
                                } else {
                                  hydrateByVehicleSelection('trailer', item.id);
                                }
                              }}
                              className="block w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                            >
                              <div className="font-medium text-gray-900">
                                {(form.vehicleKind === 'tractor' ? (item as VehicleRow).plate : (item as TrailerRow).plate)}
                              </div>
                              <div className="mt-0.5 text-xs text-gray-500">
                                {form.vehicleKind === 'tractor'
                                  ? `${(item as VehicleRow).driverName || 'Sürücü yok'} | ${(item as VehicleRow).linkedTrailerPlate || 'Dorse yok'}`
                                  : `${(item as TrailerRow).linkedVehiclePlate || 'Bağlı araç yok'}`}
                              </div>
                            </button>
                          ))}
                        {(form.vehicleKind === 'tractor' ? vehicles : trailers).length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500">Kayıt bulunamadı</div>
                        )}
                      </div>
                    )}
                    {vehicleDropdownOpen && <div className="fixed inset-0 z-10" onClick={() => setVehicleDropdownOpen(false)} />}
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Arıza Tipi</label>
                  <select value={form.reportType} onChange={(e) => setForm((current) => ({ ...current, reportType: e.target.value as ReportType }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                    {REPORT_TYPE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Sürücü Adı Soyadı</label>
                  <input value={form.driverName} onChange={(e) => setForm((current) => ({ ...current, driverName: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Sürücü Telefonu</label>
                  <input value={form.driverPhone} onChange={(e) => setForm((current) => ({ ...current, driverPhone: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Kayıt Aşaması</label>
                  <select value={form.workflowStatus} onChange={(e) => setForm((current) => ({ ...current, workflowStatus: e.target.value as WorkflowStatus }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                    {STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Arıza Kayıt Tarihi</label>
                  <input type="datetime-local" value={form.incidentDate} onChange={(e) => setForm((current) => ({ ...current, incidentDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">İl</label>
                  <input value={form.city} onChange={(e) => setForm((current) => ({ ...current, city: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">İlçe</label>
                  <input value={form.district} onChange={(e) => setForm((current) => ({ ...current, district: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">İşlem Başlama</label>
                  <input type="datetime-local" value={form.workStartAt} onChange={(e) => setForm((current) => ({ ...current, workStartAt: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">İşlem Bitiş</label>
                  <input type="datetime-local" value={form.workEndAt} onChange={(e) => setForm((current) => ({ ...current, workEndAt: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Beklenen Maliyet</label>
                  <input type="number" step="0.01" value={form.estimatedCost} onChange={(e) => setForm((current) => ({ ...current, estimatedCost: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Fatura Tutarı</label>
                  <input type="number" step="0.01" value={form.invoiceAmount} onChange={(e) => setForm((current) => ({ ...current, invoiceAmount: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Fatura No</label>
                  <input value={form.invoiceNumber} onChange={(e) => setForm((current) => ({ ...current, invoiceNumber: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Açıklama</label>
                <textarea value={form.description} onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))} rows={5} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="block rounded-xl border-2 border-dashed border-slate-300 p-4 text-center text-sm text-slate-600 cursor-pointer">
                  Fatura Görseli / PDF Seç
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)} />
                  {invoiceFile ? <div className="mt-2 font-medium text-emerald-700">{invoiceFile.name}</div> : null}
                  {invoiceDocs.length > 0 ? <div className="mt-2 text-xs text-slate-500">Yüklü: {invoiceDocs.length} belge</div> : null}
                </label>
                <label className="block rounded-xl border-2 border-dashed border-slate-300 p-4 text-center text-sm text-slate-600 cursor-pointer">
                  Kaza / Arıza Görselleri Seç
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setImageFiles(Array.from(e.target.files || []))} />
                  {imageFiles.length > 0 ? <div className="mt-2 font-medium text-emerald-700">{imageFiles.length} yeni görsel</div> : null}
                  {incidentDocs.length > 0 ? <div className="mt-2 text-xs text-slate-500">Yüklü: {incidentDocs.length} görsel</div> : null}
                </label>
              </div>

              {(invoiceDocs.length > 0 || incidentDocs.length > 0) && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <div className="font-medium text-slate-900">Yüklü Belgeler</div>
                  <div className="mt-3 space-y-2">
                    {invoiceDocs.map((doc) => <a key={`invoice-${doc.id}`} href={String(doc.file_path || doc.filePath || '').startsWith('http') ? String(doc.file_path || doc.filePath) : `${(import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${String(doc.file_path || doc.filePath).startsWith('/') ? '' : '/'}${String(doc.file_path || doc.filePath)}`} target="_blank" rel="noreferrer" className="block text-blue-600 hover:underline">Fatura: {doc.file_name || doc.fileName || 'Belge'}</a>)}
                    {incidentDocs.map((doc) => <a key={`image-${doc.id}`} href={String(doc.file_path || doc.filePath || '').startsWith('http') ? String(doc.file_path || doc.filePath) : `${(import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${String(doc.file_path || doc.filePath).startsWith('/') ? '' : '/'}${String(doc.file_path || doc.filePath)}`} target="_blank" rel="noreferrer" className="block text-blue-600 hover:underline">Görsel: {doc.file_name || doc.fileName || 'Belge'}</a>)}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button type="button" onClick={closeModal} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="button" onClick={saveReport} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailRow && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">Kaza / Arıza Detayı</h2>
              <button type="button" onClick={() => setDetailRow(null)} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <div className="space-y-5 p-6 text-sm">
              <div className="grid gap-4 md:grid-cols-2">
                <div><p className="text-gray-500">Sürücü</p><p className="mt-1 font-medium text-gray-900">{detailRow.driverName || '-'}</p></div>
                <div><p className="text-gray-500">Telefon</p><p className="mt-1 font-medium text-gray-900">{detailRow.driverPhone || '-'}</p></div>
                <div><p className="text-gray-500">Araç Tipi</p><p className="mt-1 font-medium text-gray-900">{detailRow.vehicleKind === 'trailer' ? 'Dorse' : 'Çekici'}</p></div>
                <div><p className="text-gray-500">Araç</p><p className="mt-1 font-medium text-gray-900">{detailRow.vehicleKind === 'trailer' ? detailRow.trailerPlate : detailRow.vehiclePlate}</p></div>
                <div><p className="text-gray-500">Arıza Tipi</p><p className="mt-1 font-medium text-gray-900">{REPORT_TYPE_OPTIONS.find((item) => item.value === detailRow.reportType)?.label || detailRow.reportType}</p></div>
                <div><p className="text-gray-500">Aşama</p><div className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStatusOption(detailRow.workflowStatus).className}`}>{getStatusOption(detailRow.workflowStatus).label}</div></div>
                <div><p className="text-gray-500">Kayıt Tarihi</p><p className="mt-1 font-medium text-gray-900">{formatDateTime(detailRow.incidentDate || detailRow.createdAt)}</p></div>
                <div><p className="text-gray-500">İl / İlçe</p><p className="mt-1 font-medium text-gray-900">{[detailRow.city, detailRow.district].filter(Boolean).join(' / ') || '-'}</p></div>
                <div><p className="text-gray-500">İşlem Başlama</p><p className="mt-1 font-medium text-gray-900">{formatDateTime(detailRow.workStartAt)}</p></div>
                <div><p className="text-gray-500">İşlem Bitiş</p><p className="mt-1 font-medium text-gray-900">{formatDateTime(detailRow.workEndAt)}</p></div>
                <div><p className="text-gray-500">Beklenen Maliyet</p><p className="mt-1 font-medium text-gray-900">₺{Number(detailRow.estimatedCost || 0).toLocaleString('tr-TR')}</p></div>
                <div><p className="text-gray-500">Fatura Tutarı</p><p className="mt-1 font-medium text-gray-900">₺{Number(detailRow.invoiceAmount || 0).toLocaleString('tr-TR')}</p></div>
                <div><p className="text-gray-500">Fatura No</p><p className="mt-1 font-medium text-gray-900">{detailRow.invoiceNumber || '-'}</p></div>
                <div><p className="text-gray-500">Servis Faturası</p><p className="mt-1 font-medium text-gray-900">{detailRow.invoiceDocumentId || detailRow.invoiceNumber ? 'Geldi' : 'Gelmedi'}</p></div>
              </div>
              <div><p className="text-gray-500">Açıklama</p><div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 p-4 text-gray-900">{detailRow.description || '-'}</div></div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-gray-500 mb-2">Fatura Görselleri</p>
                  <div className="grid grid-cols-2 gap-2">{detailInvoiceDocs.length > 0 ? detailInvoiceDocs.map((doc) => {
                    const url = String(doc.file_path || doc.filePath || '').startsWith('http') ? String(doc.file_path || doc.filePath) : `${(import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${String(doc.file_path || doc.filePath).startsWith('/') ? '' : '/'}${String(doc.file_path || doc.filePath)}`;
                    return /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(url) ? <img key={doc.id} src={url} alt={doc.file_name || 'Fatura'} className="h-24 w-full rounded-lg border border-gray-200 object-cover cursor-pointer hover:opacity-80" onClick={() => window.open(url, '_blank')} /> : <a key={doc.id} href={url} target="_blank" rel="noreferrer" className="block text-blue-600 hover:underline">{doc.file_name || doc.fileName || 'Belge'}</a>;
                  }) : <div className="text-sm text-gray-400">Yok</div>}</div>
                </div>
                <div>
                  <p className="text-gray-500 mb-2">Kaza / Arıza Görselleri</p>
                  <div className="grid grid-cols-2 gap-2">{detailIncidentDocs.length > 0 ? detailIncidentDocs.map((doc) => {
                    const url = String(doc.file_path || doc.filePath || '').startsWith('http') ? String(doc.file_path || doc.filePath) : `${(import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${String(doc.file_path || doc.filePath).startsWith('/') ? '' : '/'}${String(doc.file_path || doc.filePath)}`;
                    return /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(url) ? <img key={doc.id} src={url} alt={doc.file_name || 'Görsel'} className="h-24 w-full rounded-lg border border-gray-200 object-cover cursor-pointer hover:opacity-80" onClick={() => window.open(url, '_blank')} /> : <a key={doc.id} href={url} target="_blank" rel="noreferrer" className="block text-blue-600 hover:underline">{doc.file_name || doc.fileName || 'Belge'}</a>;
                  }) : <div className="text-sm text-gray-400">Yok</div>}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
